import { useMemo } from 'react';

import {
  useAddMyHomeCourse,
  useMyHomeCourses,
  useRemoveMyHomeCourse,
  useSetMyHomeCourseHidden,
  useSetMyPrimaryHomeCourse,
} from './useMemberHomeCourseApi';
import {
  CourseMinifiedDTO,
  MemberHomeCourseSummaryDTO,
} from '../services/apiClient';
import { showToast } from '../utils/showToast';
import { confirmAction } from '../utils/sweetAlertUtils';

interface HomeClubActions {
  homeCourses: MemberHomeCourseSummaryDTO[];
  isLoading: boolean;
  // Lower-cased, per CLAUDE.md: GUID casing is not guaranteed to match between the member's rows
  // and the course dataset the picker searches.
  homeCourseIds: Set<string>;
  add: (course: CourseMinifiedDTO) => Promise<void>;
  remove: (row: MemberHomeCourseSummaryDTO) => Promise<void>;
  setHidden: (row: MemberHomeCourseSummaryDTO, hidden: boolean) => void;
  setPrimary: (row: MemberHomeCourseSummaryDTO) => void;
  // The course whose hide/show or make-primary is saving, so its buttons can wait.
  savingCourseId: string | undefined;
}

// Add, remove, hide/show and make primary for the member's home clubs, shared by the desktop Home
// Courses page and the Home tab of the profile's golf lists (both are HomeCoursesTable).
export const useHomeClubActions = (): HomeClubActions => {
  const { data, isLoading } = useMyHomeCourses();
  const addHomeCourse = useAddMyHomeCourse();
  const removeHomeCourse = useRemoveMyHomeCourse();
  const setHomeCourseHidden = useSetMyHomeCourseHidden();
  const setPrimaryHomeCourse = useSetMyPrimaryHomeCourse();

  const homeCourses = useMemo(() => data ?? [], [data]);

  const homeCourseIds = useMemo(
    () =>
      new Set(
        homeCourses
          .map((row) => row.courseId?.toLowerCase())
          .filter((id): id is string => id != null),
      ),
    [homeCourses],
  );

  const add = async (course: CourseMinifiedDTO): Promise<void> => {
    if (course.id == null) {
      return;
    }

    try {
      await addHomeCourse.mutateAsync(course.id);
      showToast(
        `${course.name ?? 'Course'} added to your home courses.`,
        'success',
      );
    } catch (error: unknown) {
      showToast('Could not add that home course.', 'error');
      console.error('Could not add that home course:', error);
      // Rethrown so CoursePickerDrawer leaves the course un-ticked.
      throw error;
    }
  };

  const remove = async (row: MemberHomeCourseSummaryDTO): Promise<void> => {
    if (row.courseId == null) {
      return;
    }

    const proceed = await confirmAction({
      title: 'Remove home course?',
      text: `${row.courseName || 'This course'} will no longer be one of your home courses.`,
      icon: 'warning',
      confirmButtonText: 'Remove',
      cancelButtonText: 'Cancel',
    });

    if (!proceed) {
      return;
    }

    try {
      await removeHomeCourse.mutateAsync(row.courseId);
      showToast(
        `${row.courseName || 'Course'} removed from your home courses.`,
        'success',
      );
    } catch (error: unknown) {
      showToast('Could not remove that home course.', 'error');
      console.error('Could not remove that home course:', error);
    }
  };

  const setHidden = (
    row: MemberHomeCourseSummaryDTO,
    hidden: boolean,
  ): void => {
    if (row.courseId == null) {
      return;
    }

    setHomeCourseHidden.mutate(
      { courseId: row.courseId, hidden },
      {
        onError: (error: unknown) => {
          showToast('Could not save that change.', 'error');
          console.error('Could not change home course visibility:', error);
        },
      },
    );
  };

  const setPrimary = (row: MemberHomeCourseSummaryDTO): void => {
    if (row.courseId == null || row.isPrimary) {
      return;
    }

    setPrimaryHomeCourse.mutate(row.courseId, {
      onSuccess: () =>
        showToast(
          `${row.courseName || 'That course'} is now your primary home course.`,
          'success',
        ),
      onError: (error: unknown) => {
        showToast('Could not save that change.', 'error');
        console.error('Could not set the primary home course:', error);
      },
    });
  };

  return {
    homeCourses,
    isLoading,
    homeCourseIds,
    add,
    remove,
    setHidden,
    setPrimary,
    savingCourseId: setHomeCourseHidden.isPending
      ? setHomeCourseHidden.variables?.courseId
      : setPrimaryHomeCourse.isPending
        ? setPrimaryHomeCourse.variables
        : undefined,
  };
};
